const DietSimpleComponent = (json: IDietSimpleComponent): IDietSimpleComponentModel => {
	const { id, name } = json

	return {
		id,
		name: LangContentToModel(name),
	}
}

/**
 * Raw Materials
 */
const DietaryRawMaterialAlternativeToModel = (
	json: IDietaryRawMaterialAlternative,
): IDietaryRawMaterialAlternativeModel => {
	const { order, auto_sync, net_weight, alternative_raw_material } = json

	return {
		order,
		autoSync: auto_sync,
		netWeight: net_weight,
		alternativeRawMaterial: DietaryRawMaterialAlternativeDetailsToModel(alternative_raw_material),
	}
}

const DietComponentsRawMaterial = (json: IDietComponentsRawMaterial): IDietComponentsRawMaterialModel => {
	const {
		name,
		internal_name,
		warehouse_name,
		food_category,
		is_active,
		is_liquid,
		thermal_processing_coefficient,
		mechanical_processing_coefficient,
		allergens,
		dietary_tags,
		related_meals,
		related_semi_products,
		nutritional_parameters,
		alternatives,
		...rest
	} = json

	return {
		...rest,
		type: EDietSharedComponentType.MATERIAL,
		name: LangContentToModel(name),
		internalName: internal_name,
		warehouseName: warehouse_name,
		isActive: is_active,
		isLiquid: is_liquid,
		foodCategory: food_category,
		thermalProcessingCoefficient: thermal_processing_coefficient,
		mechanicalProcessingCoefficient: mechanical_processing_coefficient,
		allergens: allergens ? allergens.map(DietAllergen) : [],
		dietaryTags: dietary_tags ? dietary_tags.map(DietDietaryTag) : [],
		relatedMeals: related_meals ? related_meals.map(DietSimpleComponent) : [],
		relatedSemiProducts: related_semi_products ? related_semi_products.map(DietSimpleComponent) : [],
		nutritionalParameters: nutritional_parameters ? nutritional_parameters.map(NutritionalParameterWithValue) : [],
		alternatives: alternatives ? alternatives.map(DietaryRawMaterialAlternativeToModel) : [],
	}
}

/**
 * Semi Products
 */
const DietComponentsSemiProductComponentPivot = (
	json: IDietSemiProductComponentPivot,
): IDietSemiProductComponentPivotModel => {
	const {
		component_type,
		net_weight,
		raw_weight,
		weight_after_processing,
		weight_after_processing_ep,
		weight_after_processing_mech_ep,
		weight_unit,
		group_id,
		...rest
	} = json

	return {
		...rest,
		groupId: group_id ?? null,
		componentType: component_type,
		netWeight: Number.isNaN(Number(net_weight)) ? 0 : Number(net_weight),
		rawWeight: Number.isNaN(Number(raw_weight)) ? 0 : Number(raw_weight),
		weightAfterProcessing: Number.isNaN(Number(weight_after_processing)) ? 0 : Number(weight_after_processing),
		weightAfterProcessingEp: Number.isNaN(Number(weight_after_processing_ep))
			? 0
			: Number(weight_after_processing_ep),
		weightAfterProcessingMechEp: Number.isNaN(Number(weight_after_processing_mech_ep))
			? 0
			: Number(weight_after_processing_mech_ep),
		weightUnit: weight_unit,
	}
}

const DietComponentsSemiProductComponent = (
	json: IDietComponentsSemiProductComponent,
): IDietComponentsSemiProductComponentModel => {
	const {
		name,
		internal_name,
		warehouse_name,
		food_category,
		is_active,
		is_liquid,
		pivot,
		nutritional_parameters,
		mechanical_processing_coefficient,
		thermal_processing_coefficient,
		...rest
	} = json

	return {
		...rest,
		name: LangContentToModel(name),
		internalName: internal_name,
		warehouseName: warehouse_name,
		isActive: is_active,
		isLiquid: is_liquid,
		foodCategory: food_category,
		pivot: DietComponentsSemiProductComponentPivot(pivot),
		nutritionalParameters: nutritional_parameters.map(n => NutritionalParameterWithValue(n)),
		mechanicalProcessingCoefficient: mechanical_processing_coefficient,
		thermalProcessingCoefficient: thermal_processing_coefficient,
	}
}

const DietComponentsSemiProduct = (json: IDietComponentsSemiProduct): IDietComponentsSemiProductModel => {
	const {
		name,
		internal_name,
		warehouse_name,
		is_active,
		standardization_factor,
		allergens,
		dietary_tags,
		dietary_groups,
		components,
		related_meals,
		nutritional_parameters,
		updated_at,
		created_at,
		...rest
	} = json

	return {
		...rest,
		type: EDietSharedComponentType.SEMIPRODUCT,
		name: LangContentToModel(name),
		internalName: internal_name,
		warehouseName: warehouse_name,
		isActive: is_active,
		standardizationFactor: standardization_factor,
		allergens: allergens ? allergens.map(DietAllergen) : [],
		dietaryTags: dietary_tags ? dietary_tags.map(DietDietaryTag) : [],
		dietaryGroups: dietary_groups ? dietary_groups.map(DietDietaryGroup) : [],
		components: components ? components.map(DietComponentsSemiProductComponent) : [],
		relatedMeals: related_meals ? related_meals.map(DietSimpleComponent) : [],
		nutritionalParameters: nutritional_parameters ? nutritional_parameters.map(NutritionalParameterWithValue) : [],
		createdAt: created_at ? new Date(created_at) : null,
		updatedAt: updated_at ? new Date(updated_at) : null,
	}
}

export const DietSemiProductToSharedComponent = (data: IDietComponentsSemiProductModel): IDietComponentsShared => {
	const { internalName, components, relatedMeals, ...rest } = data

	return {
		...rest,
		internalName: internalName ?? '',
		type: EDietSharedComponentType.SEMIPRODUCT,
		rawWeight: '0',
		netWeight: '0',
		weightUnit: 'g',
		weightAfterProcessing: '0',
		components,
		relatedMeals,
		relatedSemiProducts: null,
	}
}

export const DietRawMaterialToSharedComponent = (data: IDietComponentsRawMaterialModel): IDietComponentsShared => {
	const { internalName, relatedMeals, relatedSemiProducts, ...rest } = data

	return {
		...rest,
		internalName: internalName ?? '',
		type: EDietSharedComponentType.MATERIAL,
		rawWeight: '0',
		netWeight: '0',
		weightUnit: 'g',
		weightAfterProcessing: '0',
		relatedMeals,
		relatedSemiProducts,
	}
}

export const Components = (json: IDietComponents): IDietComponentsModel => {
	const { dietary_raw_materials, dietary_semi_products } = json

	return {
		dietaryRawMaterials: dietary_raw_materials.map(DietComponentsRawMaterial),
		dietarySemiProducts: dietary_semi_products.map(DietComponentsSemiProduct),
	}
}
